/**
 * Global leaderboard table (M9.2, FR-10.2). Presentational: given the fetched
 * entries, renders games-played / wins / cumulative + average score, highest total
 * first. Negative cumulative scores are allowed (FR-4.7) and rendered as `-$N`.
 */

import type { LeaderboardEntry } from '@jeopardy/shared';
import { Avatar } from './Avatar';

export interface LeaderboardProps {
  entries: LeaderboardEntry[];
}

/** Format a (possibly negative) dollar score as `$N` / `-$N`. */
function money(score: number): string {
  return score < 0 ? `-$${Math.abs(score)}` : `$${score}`;
}

export function Leaderboard({ entries }: LeaderboardProps): React.JSX.Element {
  if (entries.length === 0) {
    return (
      <p className="muted" data-testid="leaderboard-empty">
        No games have been played yet — finish a game to appear here.
      </p>
    );
  }
  return (
    <table className="leaderboard" data-testid="leaderboard">
      <thead>
        <tr>
          <th className="leaderboard__rank">#</th>
          <th className="leaderboard__name">Player</th>
          <th className="leaderboard__num">Games</th>
          <th className="leaderboard__num">Wins</th>
          <th className="leaderboard__num">Total</th>
          <th className="leaderboard__num">Avg</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => (
          <tr
            key={entry.playerId}
            className="leaderboard__row"
            data-testid={`leaderboard-row-${entry.playerId}`}
          >
            <td className="leaderboard__rank">{index + 1}</td>
            <td className="leaderboard__name">
              <Avatar name={entry.displayName} avatarUrl={entry.avatarUrl} size={24} />
              {entry.displayName}
            </td>
            <td className="leaderboard__num">{entry.gamesPlayed}</td>
            <td className="leaderboard__num">{entry.wins}</td>
            <td
              className={
                'leaderboard__num' +
                (entry.totalScore < 0 ? ' leaderboard__num--negative' : '')
              }
            >
              {money(entry.totalScore)}
            </td>
            <td className="leaderboard__num">{money(entry.averageScore)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
